import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Shield, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Calendar,
  MapPin,
  User,
  Eye,
  Loader2,
  RefreshCw,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
  FileText,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { getImageUrl } from '../../utils/apiUrl';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

const STATUS_BADGES = {
  pending: { label: 'Pending', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30', icon: Clock },
  approved: { label: 'Approved', className: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  rejected: { label: 'Rejected', className: 'bg-red-500/20 text-red-400 border-red-500/30', icon: XCircle },
};

const CATEGORY_LABELS = {
  music: { label: 'Music', emoji: '🎵' },
  comedy: { label: 'Comedy', emoji: '😂' },
  sports: { label: 'Sports', emoji: '⚽' },
  weddings: { label: 'Weddings', emoji: '💒' },
  nightlife: { label: 'Nightlife', emoji: '🎉' },
  festivals: { label: 'Festivals', emoji: '🎪' },
  awards: { label: 'Awards', emoji: '🏆' },
  arts: { label: 'Arts & Theater', emoji: '🎭' },
  food: { label: 'Food & Drink', emoji: '🍽️' },
  business: { label: 'Business', emoji: '💼' },
  other: { label: 'Other', emoji: '📅' },
};

function StatCard({ title, value, subtitle, icon: Icon, color = 'primary' }) {
  const colorClasses = {
    primary: 'bg-primary/10 text-primary',
    yellow: 'bg-yellow-500/10 text-yellow-500',
    green: 'bg-green-500/10 text-green-500',
    red: 'bg-red-500/10 text-red-500',
  };

  return (
    <Card className="bg-card/50 border-border/50">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ModeratorDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [eventToReject, setEventToReject] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [statusFilter, categoryFilter]);

  const fetchData = async () => {
    setLoading(true);
    setAccessError(null);
    try {
      const statsRes = await axios.get(`${API_URL}/api/admin/moderator/stats`);
      setStats(statsRes.data);
      await fetchEvents();
    } catch (error) {
      console.error('Error fetching moderator data:', error);
      if (error.response?.status === 403) {
        setAccessError(error.response?.data?.detail || '2FA verification required');
      } else {
        toast.error('Failed to load moderation data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      let url = `${API_URL}/api/admin/moderator/events?limit=50`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (categoryFilter !== 'all') url += `&category=${categoryFilter}`;
      
      const eventsRes = await axios.get(url);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  };

  const handleApprove = async (eventId) => {
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/moderator/events/${eventId}/moderate`, 
        { action: 'approve' },
        
      );
      toast.success('Event approved!');
      fetchData();
      setSelectedEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!eventToReject) return;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/moderator/events/${eventToReject}/moderate`, 
        { action: 'reject', reason: rejectReason || 'Does not meet platform guidelines' },
        
      );
      toast.success('Event rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setEventToReject(null);
      fetchData();
      setSelectedEvent(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject event');
    } finally {
      setActionLoading(false);
    }
  };

  const handleTogglePublish = async (eventId, currentStatus) => {
    try {
      await axios.post(`${API_URL}/api/admin/moderator/events/${eventId}/toggle-publish`, {});
      toast.success(`Event ${currentStatus ? 'unpublished' : 'published'}`);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle publish status');
    }
  };

  const openRejectDialog = (eventId) => {
    setEventToReject(eventId);
    setRejectDialogOpen(true);
  };

  if (accessError) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive" className="max-w-lg mx-auto">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{accessError}</AlertDescription>
          </Alert>
          <div className="text-center mt-4">
            <Button onClick={() => window.location.href = '/admin/2fa-setup'}>
              Set up 2FA
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-primary/10">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Moderator Dashboard</h1>
              <p className="text-muted-foreground mt-1">Review and approve event submissions</p>
            </div>
          </div>
          <Button onClick={fetchData} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Pending Approval"
            value={stats?.events?.pending_approval || 0}
            subtitle="Awaiting review"
            icon={Clock}
            color="yellow"
          />
          <StatCard
            title="Approved Events"
            value={stats?.events?.approved || 0}
            subtitle="Live on platform"
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Rejected Events"
            value={stats?.events?.rejected || 0}
            subtitle="Did not meet guidelines"
            icon={XCircle}
            color="red"
          />
          <StatCard
            title="Total Events"
            value={stats?.events?.total || 0}
            subtitle={`${stats?.events?.published || 0} published`}
            icon={Calendar}
          />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
              <SelectItem value="all">All Status</SelectItem>
            </SelectContent>
          </Select>

          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.entries(CATEGORY_LABELS).map(([key, { label, emoji }]) => (
                <SelectItem key={key} value={key}>{emoji} {label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Events List */}
        <Card className="bg-card/50 border-border/50">
          <CardHeader>
            <CardTitle>Events ({events.length})</CardTitle>
            <CardDescription>Click on an event to review details</CardDescription>
          </CardHeader>
          <CardContent>
            {events.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No events found matching your filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {events.map((event) => (
                  <div 
                    key={event.event_id}
                    className={`p-4 rounded-xl border transition-all cursor-pointer hover:border-primary/50 ${
                      selectedEvent?.event_id === event.event_id ? 'border-primary bg-primary/5' : 'border-border/50'
                    }`}
                    onClick={() => setSelectedEvent(event)}
                  >
                    <div className="flex items-start gap-4">
                      {event.image_url && (
                        <img 
                          src={getImageUrl(event.image_url)} 
                          alt={event.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-semibold truncate">{event.title}</h3>
                            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                              <span>{CATEGORY_LABELS[event.category]?.emoji} {CATEGORY_LABELS[event.category]?.label || event.category}</span>
                              <span>•</span>
                              <span><User className="w-3 h-3 inline mr-1" />{event.organizer?.name}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={STATUS_BADGES[event.approval_status || 'approved']?.className}>
                              {STATUS_BADGES[event.approval_status || 'approved']?.label}
                            </Badge>
                            {event.is_published ? (
                              <Badge className="bg-green-500/20 text-green-400">Published</Badge>
                            ) : (
                              <Badge variant="secondary">Draft</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                          <span><Calendar className="w-3 h-3 inline mr-1" />{format(new Date(event.date), 'MMM d, yyyy')}</span>
                          <span><MapPin className="w-3 h-3 inline mr-1" />{event.city}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action buttons for pending events */}
                    {event.approval_status === 'pending' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          className="gap-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleApprove(event.event_id)}
                          disabled={actionLoading}
                        >
                          <ThumbsUp className="w-4 h-4" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="gap-1"
                          onClick={() => openRejectDialog(event.event_id)}
                          disabled={actionLoading}
                        >
                          <ThumbsDown className="w-4 h-4" />
                          Reject
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`/events/${event.event_id}`, '_blank')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                      </div>
                    )}

                    {/* Toggle publish for approved events */}
                    {event.approval_status === 'approved' && (
                      <div className="flex gap-2 mt-4 pt-4 border-t border-border/50" onClick={(e) => e.stopPropagation()}>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="gap-1"
                          onClick={() => handleTogglePublish(event.event_id, event.is_published)}
                        >
                          {event.is_published ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                          {event.is_published ? 'Unpublish' : 'Publish'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`/events/${event.event_id}`, '_blank')}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Event</DialogTitle>
              <DialogDescription>
                Provide a reason for rejecting this event. The organizer will be notified.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Textarea
                placeholder="Reason for rejection (e.g., 'Incomplete event details', 'Inappropriate content')"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Reject Event
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
